import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react";

import {
  getSessionStorageItem,
  setSessionStorageItem,
} from "../services/SessionStorage";

type SetValue<T> = Dispatch<SetStateAction<T>>;

/**
 * Returns a stateful value, and a function to update it in the session storage usign a "key" as a reference.
 * Also accept a initialState argument
 * @param key
 * @version 0.0.1
 * @param initialState
 */
export function useSessionStorageState<S>(
  key: string,
  initialState: S | (() => S)
): [S, SetValue<S>];

/**
 * Returns a stateful value, and a function to update it in the session storage usign a "key" as a reference.
 * @version 0.0.1
 * @param key
 */
// convenience overload when initialState argument is omitted
export function useSessionStorageState<S = undefined>(
  key: string
): [S | undefined, SetValue<S | undefined>];

export function useSessionStorageState<Store extends string>(
  key: string,
  initialState?: Store | (() => Store)
): [Store | undefined, SetValue<Store>] {
  // Validates that the initial state is a function, in this case it is executed to obtain the value of the state
  const defaultValue =
    initialState instanceof Function ? initialState() : initialState;

  // add useCallback to avoid reading session storage on every re-render
  const sessionStorageData = useCallback(
    (): Store =>
      (parseStringToJson(getSessionStorageItem(key)) as Store) ?? defaultValue,
    [defaultValue, key]
  );

  // Session storage data or initial state argument is default loaded
  const [state, setValue] = useState(sessionStorageData);

  useEffect(() => {
    // Save state to session storage when state changes
    setSessionStorageItem(key, parseJsonToString(state));
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [state]);

  return [state, setValue];
}

function parseStringToJson<T>(value: string | null | undefined): T | undefined {
  try {
    return value === "undefined" || value === null
      ? undefined
      : JSON.parse(value ?? "");
  } catch (e) {
    console.warn(`[parseStringToJson] Error parsing: ${{ value }} - `, e);
    return undefined;
  }
}

function parseJsonToString(
  value: string | null | undefined
): string | undefined {
  try {
    return value === "undefined" || value === null
      ? undefined
      : JSON.stringify(value ?? "");
  } catch (e) {
    console.warn(`[parseJsonToString] Error parsing: ${{ value }} - `, e);
    return undefined;
  }
}
